// src/app/shared/my-goals-dialog/my-goals-dialog.component.ts
import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { firstValueFrom } from 'rxjs';
import { GoalsService } from 'src/app/services/goals.service';
import { authService } from 'src/app/services/auth.service';

interface GoalWithProgress {
  id: number;
  name: string;
  entity: string;
  goal_type: string;
  frequency: string;
  planned_count: number;
  actual_count: number;
  variance: number;
  achievement_pct: number;
  current_period?: any;
}

@Component({
  selector: 'app-my-goals-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDividerModule,
  ],
  template: `
    <div class="my-goals-dialog">
      <!-- Header -->
      <div class="dialog-header">
        <div class="header-title">
          <mat-icon>emoji_objects</mat-icon>
          <h2>My Goals</h2>
        </div>
        <button mat-icon-button (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Loading State -->
      <div class="loading-container" *ngIf="isLoading">
        <mat-spinner diameter="40"></mat-spinner>
      </div>

      <!-- Goals List -->
      <div class="goals-list" *ngIf="!isLoading">
        <!-- Empty State -->
        <div class="empty-state" *ngIf="goals.length === 0">
          <mat-icon>flag</mat-icon>
          <p>No goals assigned to you</p>
          <span>Goals assigned to you will appear here</span>
        </div>

        <!-- Goal Cards -->
        <div 
          class="goal-card" 
          [class.clickable]="isAdmin"
          *ngFor="let goal of goals" 
          (click)="onGoalClick(goal)">
          <div class="goal-header">
            <div class="goal-info">
              <span class="goal-name">{{ goal.name }}</span>
              <span class="goal-meta">
                {{ getEntityLabel(goal.entity) }} • {{ getGoalTypeLabel(goal.goal_type) }}
              </span>
            </div>
            <span class="frequency-badge">{{ goal.frequency }}</span>
          </div>

          <div class="goal-progress">
            <div class="progress-stats">
              <span class="actual">{{ goal.actual_count || 0 }}</span>
              <span class="separator">/</span>
              <span class="planned">{{ goal.planned_count }}</span>
            </div>
            <div class="progress-bar-container">
              <div 
                class="progress-bar-fill"
                [style.width.%]="getProgressWidth(goal)"
                [class]="getProgressClass(goal)">
              </div>
            </div>
            <div class="progress-footer">
              <span class="percentage" [class]="getProgressClass(goal)">
                {{ goal.achievement_pct || 0 }}%
              </span>
              <span class="variance" [class]="getVarianceClass(goal)">
                {{ (goal.variance || 0) >= 0 ? '+' : '' }}{{ goal.variance || 0 }}
              </span>
            </div>
          </div>

          <div class="goal-period" *ngIf="goal.current_period">
            <mat-icon>calendar_today</mat-icon>
            <span>{{ goal.current_period.period_label }}</span>
          </div>
        </div>
      </div>

      <!-- Footer - Admin Only -->
      <div class="dialog-footer" *ngIf="!isLoading && goals.length > 0 && isAdmin">
        <button mat-stroked-button (click)="viewAllGoals()">
          View All Goals
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }

    .my-goals-dialog {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: var(--surface-primary, #fff);
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid var(--border-light, #e5e7eb);
      background: var(--light-primary, rgba(0, 161, 255, 0.08));
    }

    .header-title {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .header-title mat-icon {
      color: var(--form-primary-color, #00a1ff);
      font-size: 28px;
      width: 28px;
      height: 28px;
    }

    .header-title h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--text-primary, #1f2937);
    }

    .dialog-header button {
      color: var(--text-secondary, #6b7280);
    }

    .dialog-header button:hover {
      background: var(--surface-hover, rgba(0, 0, 0, 0.04));
    }

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
      padding: 40px;
    }

    .goals-list {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      text-align: center;
    }

    .empty-state mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      color: var(--text-muted, #9ca3af);
      margin-bottom: 16px;
    }

    .empty-state p {
      margin: 0 0 8px;
      font-size: 16px;
      font-weight: 500;
      color: var(--text-primary, #374151);
    }

    .empty-state span {
      font-size: 13px;
      color: var(--text-secondary, #6b7280);
    }

    .goal-card {
      background: var(--surface-secondary, #f9fafb);
      border: 1px solid var(--border-light, #e5e7eb);
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 12px;
      transition: all 0.2s ease;
    }

    .goal-card.clickable {
      cursor: pointer;
    }

    .goal-card.clickable:hover {
      border-color: var(--form-primary-color, #00a1ff);
      box-shadow: 0 4px 12px var(--form-card-shadow, rgba(0, 161, 255, 0.15));
      transform: translateY(-2px);
    }

    .goal-card:last-child {
      margin-bottom: 0;
    }

    .goal-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
    }

    .goal-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .goal-name {
      font-size: 15px;
      font-weight: 600;
      color: var(--text-primary, #1f2937);
    }

    .goal-meta {
      font-size: 12px;
      color: var(--text-secondary, #6b7280);
    }

    .frequency-badge {
      font-size: 11px;
      font-weight: 500;
      padding: 4px 8px;
      border-radius: 12px;
      background: var(--light-primary, rgba(0, 161, 255, 0.1));
      color: var(--form-primary-color, #00a1ff);
      text-transform: uppercase;
    }

    .goal-progress {
      margin-bottom: 4px;
    }

    .progress-stats {
      display: flex;
      align-items: baseline;
      margin-bottom: 8px;
    }

    .progress-stats .actual {
      font-size: 24px;
      font-weight: 700;
      color: var(--text-primary, #1f2937);
    }

    .progress-stats .separator {
      font-size: 16px;
      color: var(--text-muted, #9ca3af);
      margin: 0 4px;
    }

    .progress-stats .planned {
      font-size: 16px;
      color: var(--text-secondary, #6b7280);
    }

    .progress-bar-container {
      height: 8px;
      background: var(--form-border-color, #e5e7eb);
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 8px;
    }

    .progress-bar-fill {
      height: 100%;
      border-radius: 4px;
      transition: width 0.3s ease;
    }

    .progress-bar-fill.success {
      background: var(--dashboard-success-color, #10b981);
    }

    .progress-bar-fill.exact {
      background: var(--form-primary-color, #3b82f6);
    }

    .progress-bar-fill.danger {
      background: var(--form-error-color, #ef4444);
    }

    .progress-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .progress-footer .percentage {
      font-size: 13px;
      font-weight: 600;
    }

    .progress-footer .percentage.success {
      color: var(--dashboard-success-color, #10b981);
    }

    .progress-footer .percentage.exact {
      color: var(--form-primary-color, #3b82f6);
    }

    .progress-footer .percentage.danger {
      color: var(--form-error-color, #ef4444);
    }

    .progress-footer .variance {
      font-size: 12px;
      font-weight: 500;
    }

    .progress-footer .variance.positive {
      color: var(--dashboard-success-color, #10b981);
    }

    .progress-footer .variance.exact {
      color: var(--form-primary-color, #3b82f6);
    }

    .progress-footer .variance.negative {
      color: var(--form-error-color, #ef4444);
    }

    .goal-period {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--border-light, #e5e7eb);
    }

    .goal-period mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
      color: var(--text-muted, #9ca3af);
    }

    .goal-period span {
      font-size: 12px;
      color: var(--text-secondary, #6b7280);
    }

    .dialog-footer {
      padding: 16px 20px;
      border-top: 1px solid var(--border-light, #e5e7eb);
      display: flex;
      justify-content: center;
      background: var(--surface-primary, #fff);
    }

    .dialog-footer button {
      width: 100%;
      color: var(--form-primary-color, #00a1ff);
      border-color: var(--form-primary-color, #00a1ff);
    }

    .dialog-footer button:hover {
      background: var(--light-primary, rgba(0, 161, 255, 0.08));
    }
  `]
})
export class MyGoalsDialogComponent implements OnInit {
  goals: GoalWithProgress[] = [];
  isLoading = true;
  isAdmin = false;

  constructor(
    private dialogRef: MatDialogRef<MyGoalsDialogComponent>,
    private goalsService: GoalsService,
    private router: Router,
    private authService: authService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isAdmin = this.authService.getUserRole() === 'ADMIN';
  }

  async ngOnInit() {
    await this.loadMyGoals();
  }

  async loadMyGoals() {
    this.isLoading = true;
    try {
      const response: any = await firstValueFrom(
        this.goalsService.getMyGoals()
      );
      this.goals = response?.data || [];
    } catch (error) {
      console.error('Error loading goals:', error);
      this.goals = [];
    } finally {
      this.isLoading = false;
    }
  }

  getEntityLabel(entity: string): string {
    const map: Record<string, string> = {
      LEAD: 'Lead',
      ACTIVITY: 'Activity',
      APPOINTMENT: 'Appointment'
    };
    return map[entity] || entity;
  }

  getGoalTypeLabel(goalType: string): string {
    const map: Record<string, string> = {
      ASSIGNED: 'Assigned',
      PROGRESSED: 'Progressed',
      WON: 'Won',
      ENROLLED: 'Enrolled',
      ADDED: 'Added',
      COMPLETED: 'Completed'
    };
    return map[goalType] || goalType;
  }

  getProgressWidth(goal: GoalWithProgress): number {
    const pct = goal.achievement_pct || 0;
    return pct > 100 ? 100 : pct;
  }

  getProgressClass(goal: GoalWithProgress): string {
    const actual = goal.actual_count || 0;
    const planned = goal.planned_count || 0;

    if (actual > planned) return 'success';
    if (actual === planned && actual > 0) return 'exact';
    return 'danger';
  }

  getVarianceClass(goal: GoalWithProgress): string {
    const variance = goal.variance || 0;
    if (variance > 0) return 'positive';
    if (variance === 0) return 'exact';
    return 'negative';
  }

  onGoalClick(goal: GoalWithProgress) {
    if (this.isAdmin) {
      this.close();
      this.router.navigate(['/goals', goal.id]);
    }
  }

  viewAllGoals() {
    if (this.isAdmin) {
      this.close();
      this.router.navigate(['/goals']);
    }
  }

  close() {
    this.dialogRef.close();
  }
}